import retry from 'p-retry';

import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';
import { UserService } from '../user/userService';
import { AdditionalQuestion } from '../../types/guest';

interface UpdateGuestsDataPayload {
  data: Array<{
    id: string;
    name: string;
    surname: string;
    presence: boolean | undefined;
    connection: string;
    connectedGuestId: string;
    additionalQuestions: AdditionalQuestion[];
  }>;
  abortSignal: AbortSignal;
  removeQuestionTemplate?: string;
}

export class GuestService {
  private static apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'api';

  public static async getGuestsData(): Promise<Array<Record<string, string | number>>> {
    const abortController = new AbortController();

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/guests`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message, abortController);
        }

        const data = await response.json();
        return data.data.map(
          (guest: {
            id: string;
            name: string;
            surname: string;
            presence: boolean;
            connection: string;
            connectedGuestId: string;
            additionalQuestions: string | AdditionalQuestion[];
          }) => ({
            ...guest,
            additionalQuestions:
              typeof guest.additionalQuestions === 'string'
                ? JSON.parse(guest.additionalQuestions)
                : guest.additionalQuestions || [],
          }),
        );
      },
      {
        retries: 2,
        signal: abortController.signal,
      },
    );

    return result;
  }

  public static async updateGuestsData(payload: UpdateGuestsDataPayload): Promise<void> {
    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/guests`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          body: JSON.stringify({
            data: payload.data,
            removeQuestionTemplate: payload.removeQuestionTemplate,
          }),
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }
      },
      {
        retries: 3,
        onFailedAttempt: (error) => {
          console.error(`Attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`);
        },
      },
    );

    return result;
  }

  public static async sendShareLinks(guestEmails: Array<string>): Promise<void> {
    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/users/guests/links/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
            Accept: 'application/json',
          },
          body: JSON.stringify({
            emails: guestEmails,
          }),
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }
      },
      {
        retries: 2,
      },
    );

    return result;
  }

  public static async createShareLink(): Promise<string> {
    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/users/guests/links`, {
          method: 'POST',
          body: JSON.stringify({}),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
            Accept: 'application/json',
          },
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }

        return response.json();
      },
      {
        retries: 2,
      },
    );

    return result.link;
  }

  private static async handleError(status: number, message: string, abortController?: AbortController): Promise<void> {
    if (status === 401) {
      const userStore = useUserTokensStore();

      if (userStore.getLoggedInUserRefreshToken) {
        const response = await UserService.refreshToken({ refreshToken: userStore.getLoggedInUserRefreshToken });

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response;

        userStore.setUserTokens({
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        throw new Error('Token expired. Retrying...');
      }
    } else if (status === 404 && abortController) {
      abortController.abort('Resource not found.');
    } else {
      throw new Error(message);
    }
  }
}
